import { Handle, Position } from '@xyflow/react';
import type { NodeBase } from '@/types/assets';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Server,
  Cpu,
  Shield,
  Cloud,
  Building,
  Users,
  Database,
  Router,
  Wifi,
  HardDrive,
} from 'lucide-react';
import { useAssetsStore } from '@/stores/assetsStore';
import { getCriticalityColor, normalizeCriticality, getDomainColor } from '@/lib/utils';

interface AssetNodeData {
  id: NodeBase['id'];
  type: NodeBase['type'] | 'asset' | 'cloud' | 'datastore';
  label: NodeBase['label'];
  domain?: NodeBase['domain'];
  purdue_level?: NodeBase['purdue_level'];
  category?: NodeBase['category'];
  parentId?: NodeBase['parentId'];
  position?: NodeBase['position'];
  meta?: NodeBase['meta'];
}

interface AssetNodeProps {
  data: AssetNodeData;
  selected: boolean;
}

const getNodeIcon = (type: AssetNodeData['type'], category?: string) => {
  switch (type) {
    case 'site':
      return <Building className="h-4 w-4" />;
    case 'zone':
      return <Shield className="h-4 w-4" />;
    case 'cloud':
      return <Cloud className="h-4 w-4" />;
    case 'supplier':
      return <Users className="h-4 w-4" />;
    case 'datastore':
      return <Database className="h-4 w-4" />;
    case 'asset':
    case 'system':
      if (category === 'Switch' || category === 'Router') return <Router className="h-4 w-4" />;
      if (category === 'AP') return <Wifi className="h-4 w-4" />;
      if (category === 'Server' || category === 'VM') return <Server className="h-4 w-4" />;
      if (category === 'PLC' || category === 'HMI') return <Cpu className="h-4 w-4" />;
      if (category?.includes('DB') || category?.includes('Database')) return <Database className="h-4 w-4" />;
      if (category?.includes('Storage') || category?.includes('NAS')) return <HardDrive className="h-4 w-4" />;
      return <Server className="h-4 w-4" />;
    default:
      return <Server className="h-4 w-4" />;
  }
};


export function AssetNode({ data, selected }: AssetNodeProps) {
  const { getRisksByNode, getTasksByNode, setSelectedNode } = useAssetsStore();

  const risks = getRisksByNode(data.id);
  const tasks = getTasksByNode(data.id);
  const openRisks = risks.filter((risk) => risk.status === 'Open');
  const dueTasks = tasks.filter((task) => task.status === 'Due' || task.status === 'Overdue');

  const handleClick = () => {
    setSelectedNode(data.id);
  };

  return (
    <Card
      className={`min-w-[200px] cursor-pointer transition-all ${selected ? 'ring-2 ring-primary shadow-lg' : 'hover:shadow-md'}`}
      onClick={handleClick}
    >
      <div className="p-3">
        <div className="flex items-center gap-2 mb-2">
          {getNodeIcon(data.type, data.category)}
          <div className="flex-1">
            <div className="font-medium text-sm truncate">{data.label}</div>
            <div className="text-xs text-muted-foreground capitalize">{data.type}</div>
          </div>
        </div>

        <div className="flex flex-wrap gap-1 mb-2">
          {data.domain && (
            <Badge variant="outline" className={getDomainColor(data.domain)}>
              {data.domain}
            </Badge>
          )}
          {data.purdue_level && (
            <Badge variant="outline" className="text-xs">
              {data.purdue_level}
            </Badge>
          )}
          {data.meta?.criticality && (
            <Badge variant="outline" className={getCriticalityColor(data.meta.criticality)}>
              {normalizeCriticality(data.meta.criticality) || data.meta.criticality}
            </Badge>
          )}
        </div>

        {(openRisks.length > 0 || dueTasks.length > 0) && (
          <div className="flex gap-1">
            {openRisks.length > 0 && (
              <Badge variant="danger" className="text-xs">
                {openRisks.length} Risk{openRisks.length > 1 ? 's' : ''}
              </Badge>
            )}
            {dueTasks.length > 0 && (
              <Badge variant="secondary" className="text-xs bg-warning/10 text-warning-foreground border-warning/20">
                {dueTasks.length} Due
              </Badge>
            )}
          </div>
        )}

        {data.meta?.ip && <div className="text-xs text-muted-foreground mt-1">{data.meta.ip}</div>}
      </div>

      <Handle
        id="top"
        type="source"
        position={Position.Top}
        isConnectableStart={true}
        isConnectableEnd={true}
        className="w-3 h-3 border-2 border-primary bg-background hover:bg-primary/20"
      />
      <Handle
        id="right"
        type="source"
        position={Position.Right}
        isConnectableStart={true}
        isConnectableEnd={true}
        className="w-3 h-3 border-2 border-primary bg-background hover:bg-primary/20"
      />
      <Handle
        id="bottom"
        type="source"
        position={Position.Bottom}
        isConnectableStart={true}
        isConnectableEnd={true}
        className="w-3 h-3 border-2 border-primary bg-background hover:bg-primary/20"
      />
      <Handle
        id="left"
        type="source"
        position={Position.Left}
        isConnectableStart={true}
        isConnectableEnd={true}
        className="w-3 h-3 border-2 border-primary bg-background hover:bg-primary/20"
      />
    </Card>
  );
}